<template>
    <div class="campaign-detail" v-if="campaign">
        <h2>{{ campaign.title }}</h2>
        <img :src="campaign.image" :alt="campaign.title" />
        <div class="accordion">
            <button class="accordion-toggle" @click="toggleAccordion">
                Detaylar
            </button>
            <div class="accordion-content" v-show="isAccordionOpen">
                <p v-for="(line, index) in formattedDetails" :key="index">{{ line }}</p>
            </div>
            <div class="step-indicators">
                <div v-for="(stepName, index) in steps" :key="index" class="step-indicator"
                    :class="{ active: step === stepName }">
                    <span class="step-number">{{ index + 1 }}</span>
                </div>
            </div>
            <div v-if="step === 'receipt-upload'" class="receipt-section" :class="{ 'mobile-layout': isMobile }">
                <div class="info-section">
                    <receipt-information></receipt-information>
                </div>
                <div class="upload-section">
                    <h3>Adım 1: Fiş Yükle</h3>
                    <p v-if="!uploadedReceipt">Fişinizi yükleyin ve kazanın!</p>
                    <input type="file" accept="image/*" ref="fileInput" style="display: none"
                        @change="handleFileUpload" />
                    <div v-if="!uploadedReceipt">
                        <button class="receipt-upload" @click="showUploadOptions" :disabled="isLoading">
                            <span v-if="isLoading" class="loader"></span>
                            <span v-else>Fiş Yükle</span>
                        </button>
                    </div>
                    <div v-else>
                        <img :src="uploadedReceipt" alt="Uploaded Receipt" class="uploaded-receipt" />
                        <button class="continue" @click="proceedToSmsVerification" :disabled="isLoading">
                            <span v-if="isLoading" class="loader continue"></span>
                            <span class="continue" v-else>Devam Et</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Modal for upload options -->
            <div v-if="showModal" class="upload-modal">
                <div class="modal-content">
                    <h4>Fiş Yükleme Seçenekleri</h4>
                    <p>Lütfen bir seçenek belirleyin:</p>
                    <button @click="openCamera">Kameradan Çek</button>
                    <button @click="triggerFileInput">Galeriden Seç (Fotoğraf veya Dosya)</button>
                    <button class="close-modal" @click="closeModal">İptal</button>
                </div>
            </div>

            <div v-if="step === 'sms-verification'" class="sms-verification-section">
                <h3>Adım 2: Telefon Numarası Doğrulama</h3>
                <p>Lütfen telefon numaranızı girin ve doğrulama kodunu alın.</p>
                <div class="phone-input-wrapper">
                    <span class="phone-prefix">+90</span>
                    <input type="tel" v-model="phoneNumber" placeholder="5XX XXX XXXX" maxlength="10"
                        class="phone-input" />
                </div>
                <button class="get-verif-code" @click="sendSmsVerification" :disabled="isLoading">
                    <span v-if="isLoading" class="loader"></span>
                    <span v-else>Doğrulama Kodu Gönder</span>
                </button>
                <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
            </div>
            <div v-if="step === 'code-verification'" class="code-verification-section">
                <h3>Adım 3: Doğrulama Kodu Girin</h3>
                <p>Telefonunuza gönderilen 4 haneli doğrulama kodunu girin.</p>
                <div class="code-input-wrapper" @input="handleAutoFill">
                    <input v-for="(digit, index) in verificationCode" :key="index" type="text" maxlength="1"
                        inputmode="numeric" class="code-input" v-model="verificationCode[index]"
                        @input="handleCodeInput(index, $event)" ref="codeInput" autocomplete="one-time-code" />
                </div>
                <p v-if="timer > 0">Kalan süre: {{ timer }} saniye</p>
                <button class="verify-button confirm-verif-code" @click="verifyCode" :disabled="isLoading">
                    <span v-if="isLoading" class="loader"></span>
                    <span v-else>Doğrula</span>
                </button>
                <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
            </div>
            <div v-if="step === 'additional-form'" class="form-section">
                <h3>Adım 4: Bilgilerinizi Tamamlayın</h3>
                <p>Lütfen aşağıdaki bilgileri doldurun.</p>
                <form @submit.prevent="submitForm" class="styled-form">
                    <div class="form-group">
                        <label for="firstName">Adınız</label>
                        <input id="firstName" v-model="firstName" placeholder="Adınız" class="form-input" />
                    </div>
                    <div class="form-group">
                        <label for="lastName">Soyadınız</label>
                        <input id="lastName" v-model="lastName" placeholder="Soyadınız" class="form-input" />
                    </div>
                    <div class="form-group">
                        <label for="email">E-posta Adresiniz</label>
                        <input id="email" v-model="email" type="email" placeholder="E-posta Adresiniz"
                            class="form-input" />
                    </div>
                    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
                    <button class="submit-button final" type="submit" :disabled="isLoading">
                        <span v-if="isLoading" class="loader"></span>
                        <span v-else>Gönder</span>
                    </button>
                </form>
            </div>
            <div v-if="step === 'thank-you'" class="thank-you-section">
                <h3>Teşekkürler!</h3>
                <p>Katılımınız için teşekkür ederiz. Kampanyamıza katıldığınız için mutluyuz!</p>
                <button class="thank-you-button" @click="goToHomePage">Ana Sayfaya Dön</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ReceiptInformation from '@/components/ReceiptInformation.vue';

export default {
    components: {
        ReceiptInformation,
    },

    data() {
        return {
            campaign: null, // Initialize as null
            isAccordionOpen: false, // Track accordion state
            step: 'receipt-upload', // Track the current step of the process
            uploadedReceipt: null, // Store the uploaded receipt image
            uploadedFile: null, // Store the uploaded receipt file
            referenceNumber: null, // Store the reference number from the API
            phoneNumber: '', // Store the user's phone number (10 digits only)
            verificationCode: ['', '', '', ''], // Store the 4-digit verification code
            firstName: '',
            lastName: '',
            email: '',
            customerToken: null,
            timer: 180, // Timer for SMS verification
            timerInterval: null, // Interval reference for the timer
            errorMessage: '', // Store error messages
            isMobile: false, // Track if the user is on a mobile device
            steps: ['receipt-upload', 'sms-verification', 'code-verification'], // Steps in the process (excluding additional-form and thank-you)
            isLoading: false, // Track loading state
            showModal: false, // Track modal visibility
        };
    },
    computed: {
        formattedDetails() {
            return this.campaign.detailsText.split('\n'); // Split detailsText by \n
        },
    },
    methods: {
        async fetchCampaignFromStorage(id) {
            const campaigns = JSON.parse(localStorage.getItem('campaigns')) || [];
            this.campaign = campaigns.find(campaign => campaign.id === id);

            if (!this.campaign) {
                const response = await fetch('https://cekkazan-web-be.jsnp.io/api/getCampaigns');
                const data = await response.json();
                const formattedCampaigns = data.results.map(campaign => ({
                    ...campaign,
                    image: `http://api.cekkazanapp.com${campaign.image}`, // Prepend base URL to image
                    start: this.formatDate(campaign.start), // Format start date
                    end: this.formatDate(campaign.end), // Format end date
                }));
                this.campaign = formattedCampaigns.find(campaign => campaign.id === id);
                localStorage.setItem('campaigns', JSON.stringify(formattedCampaigns));
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`; // Return in DD.MM.YYYY format
        },
        startUserJourney() {
            console.log(`User journey started for campaign: ${this.campaign.title}`);
            this.$router.push(`/journey/${this.campaign.id}`); // Navigate to the journey page
        },
        toggleAccordion() {
            this.isAccordionOpen = !this.isAccordionOpen; // Toggle accordion state
        },
        showUploadOptions() {
            if (this.isMobile) {
                this.showModal = true; // Show modal on mobile
            } else {
                this.triggerFileInput(); // Directly trigger file input on desktop
            }
        },
        triggerFileInput() {
            this.showModal = false; // Close modal
            this.$refs.fileInput.click(); // Trigger the hidden file input
        },
        openCamera() {
            this.showModal = false; // Close modal
            const cameraInput = document.createElement('input');
            cameraInput.type = 'file';
            cameraInput.accept = 'image/*';
            cameraInput.capture = 'environment'; // Explicitly open the camera
            cameraInput.addEventListener('change', this.handleFileUpload);
            cameraInput.click();
        },
        closeModal() {
            this.showModal = false; // Close modal
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.uploadedFile = file; // Store the file object
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.uploadedReceipt = e.target.result; // Store the uploaded image as a base64 string for preview
                };
                reader.readAsDataURL(file);
            }
        },
        startTimer() {
            this.timer = 180; // Reset timer to 180 seconds
            this.timerInterval = setInterval(() => {
                if (this.timer > 0) {
                    this.timer--;
                } else {
                    clearInterval(this.timerInterval);
                }
            }, 1000);
        },
        async proceedToSmsVerification() {
            if (!this.uploadedFile || !this.campaign) {
                this.errorMessage = 'Fiş yüklenmedi veya kampanya bilgisi eksik.';
                return;
            }

            this.isLoading = true;
            try {
                const formData = new FormData();
                formData.append('receipt', this.uploadedFile); // Add the receipt file
                formData.append('campaignId', this.campaign.id); // Add the campaign ID

                const response = await axios.post('https://cekkazan-web-be.jsnp.io/api/uploadReceipt', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.status) {
                    this.referenceNumber = response.data.referenceNumber; // Store the reference number
                    console.log('Reference Number:', this.referenceNumber);
                    this.step = 'sms-verification';
                } else {
                    this.errorMessage = 'Fiş yükleme başarısız. Lütfen tekrar deneyin.';
                }
            } catch (error) {
                this.errorMessage = 'Fiş yüklenirken bir hata oluştu.';
                console.error('Error uploading receipt:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async sendSmsVerification() {
            if (!this.phoneNumber || this.phoneNumber.length !== 10) {
                this.errorMessage = 'Geçerli bir telefon numarası giriniz.';
                return;
            }

            this.isLoading = true;
            try {
                const response = await axios.post('https://cekkazan-web-be.jsnp.io/api/sendSms', {
                    phoneNumber: `+90${this.phoneNumber}`,
                });

                if (response.data.response) {
                    console.log('SMS sent successfully:', response.data);
                    this.step = 'code-verification'; // Proceed to the code verification step
                    this.startTimer(); // Start the timer
                } else {
                    this.errorMessage = 'SMS gönderimi başarısız. Lütfen tekrar deneyin.';
                }
            } catch (error) {
                this.errorMessage = 'SMS gönderilirken bir hata oluştu.';
                console.error('Error sending SMS verification:', error);
            } finally {
                this.isLoading = false;
            }
        },
        handleCodeInput(index, event) {
            const value = event.target.value;

            // Update the current input field
            this.verificationCode[index] = value;

            // Move to the next input field if a single digit is entered
            if (value.length === 1 && index < this.verificationCode.length - 1) {
                this.$refs.codeInput[index + 1].focus();
            }

            // Move to the previous input field if backspace is pressed
            if (value.length === 0 && index > 0) {
                this.$refs.codeInput[index - 1].focus();
            }
        },
        handleAutoFill(event) {
            const inputValues = Array.from(event.currentTarget.querySelectorAll('input')).map(input => input.value);
            if (inputValues.join('').length === this.verificationCode.length) {
                this.verificationCode = inputValues;
            }
        },
        async verifyCode() {
            const code = this.verificationCode.join('');
            if (code.length !== 4) {
                this.errorMessage = 'Lütfen geçerli bir 4 haneli kod giriniz.';
                return;
            }

            this.isLoading = true;
            try {
                const response = await axios.post('https://cekkazan-web-be.jsnp.io/api/mobileVerification', {
                    referenceNumber: this.referenceNumber,
                    phoneNumber: `+90${this.phoneNumber}`,
                    verificationCode: code,
                });

                if (response.data.status) {
                    console.log('Code verified successfully:', response.data);
                    const { customerToken, isDone } = response.data;
                    this.customerToken = customerToken; // Store the customer token

                    if (isDone) {
                        this.step = 'thank-you';
                        window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({
                                event: 'participation',
                                campaign_id: this.$route.params.id,
                                phone: `+90`+ this.phoneNumber,
                            });
                    } else {
                        this.step = 'additional-form';
                    }
                } else {
                    this.errorMessage = 'Doğrulama kodu hatalı. Lütfen tekrar deneyin.';
                }
            } catch (error) {
                this.errorMessage = 'Kod doğrulama sırasında bir hata oluştu.';
                console.error('Error verifying code:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async submitForm() {
            if (!this.firstName || !this.lastName || !this.email) {
                this.errorMessage = 'Lütfen tüm alanları doldurun.';
                return;
            }

            this.isLoading = true;
            try {
                const response = await axios.post('https://cekkazan-web-be.jsnp.io/api/fillTheForm', {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    customerToken: this.customerToken,
                    referenceNumber: this.referenceNumber,
                });

                if (response.data.status) {
                    console.log('Form submitted successfully:', response.data);
                    this.step = 'thank-you';
                } else {
                    if (response.data.message === 'Email already used!') {
                        this.errorMessage = 'Bu e-posta adresi zaten kullanılmış. Lütfen başka bir e-posta adresi deneyin.';
                    } else {
                        this.errorMessage = 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.';
                    }
                }
            } catch (error) {
                this.errorMessage = 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.';
                console.error('Error submitting form:', error);
            } finally {
                this.isLoading = false;
            }
        },
        goToHomePage() {
            this.$router.push('/'); // Navigate to the home page
        },
        checkIfMobile() {
            this.isMobile = window.innerWidth <= 768; // Set isMobile based on screen width
        },
    },
    watch: {
        step(newStep) {
            if (newStep !== 'code-verification' && this.timerInterval) {
                clearInterval(this.timerInterval); // Clear timer when leaving code verification step
            }
        },
    },
    created() {
        const campaignId = this.$route.params.id; // Get campaign ID from route params
        this.fetchCampaignFromStorage(Number(campaignId)); // Fetch the campaign from localStorage
        this.checkIfMobile(); // Check if the user is on mobile when the component is created
        window.addEventListener('resize', this.checkIfMobile); // Update on window resize
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkIfMobile); // Clean up event listener
    },
};
</script>

<style>
.campaign-detail {
    padding: 2rem;
    text-align: center;
    font-family: 'Poppins', sans-serif;
}

.campaign-detail h2 {
    font-size: 2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
}

.campaign-detail img {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
    border-radius: 8px;
}

.accordion {
    margin-top: 1rem;
    text-align: left;
}

.accordion-toggle {
    background-color: #ea5610;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    text-align: left;
}

.accordion-toggle:hover {
    background-color: #d14e0e;
}

.accordion-content {
    margin-top: 0.5rem;
    padding: 1rem;
    border: 1px solid #ddd;
    background-color: #eef5f8;
    border-radius: 4px;
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    color: #444;
}

.step-indicators {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 1rem;
}

.step-indicator {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid #ddd;
    border-radius: 50%;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    color: #aaa;
    background-color: #f9f9f9;
    transition: all 0.3s ease;
}

.step-indicator.active {
    border-color: #ea5610;
    color: #fff;
    background-color: #ea5610;
}

.step-number {
    display: inline-block;
}

.receipt-section {
    display: flex;
    gap: 2rem;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 1rem;
}

.receipt-section.mobile-layout {
    flex-direction: column;
    /* Stack sections vertically on mobile */
    gap: 1rem;
}

.info-section,
.upload-section {
    flex: 1;
}

.upload-section h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.upload-section p {
    margin-bottom: 1rem;
}

.upload-section button {
    background-color: #ea5610;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
}

.upload-section button:hover {
    background-color: #d14e0e;
}

.uploaded-receipt {
    max-width: 100%;
    height: auto;
    margin-bottom: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
}

.sms-verification-section {
    margin-top: 2rem;
    text-align: center;
}

.phone-input-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
}

.phone-prefix {
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px 0 0 4px;
    padding: 0.5rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    color: #333;
}

.phone-input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-left: none;
    border-radius: 0 4px 4px 0;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
}

.sms-verification-section button {
    background-color: #ea5610;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
}

.sms-verification-section button:hover {
    background-color: #d14e0e;
}

.sms-verification-section p {
    margin-top: 0.5rem;
    font-size: 1rem;
    color: #333;
}

.error-message {
    color: red;
    font-size: 0.9rem;
    margin-top: 0.5rem;
}

.code-verification-section {
    margin-top: 2rem;
    text-align: center;
}

.code-input-wrapper {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.code-input {
    width: 3rem;
    height: 3rem;
    text-align: center;
    font-size: 1.5rem;
    font-family: 'Poppins', sans-serif;
    border: 1px solid #ddd;
    border-radius: 4px;
    outline: none;
    transition: border-color 0.3s ease;
}

.code-input:focus {
    border-color: #ea5610;
    box-shadow: 0 0 4px rgba(234, 86, 16, 0.5);
}

.verify-button {
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 0.7rem 1.5rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.verify-button:hover {
    background-color: #45a049;
}

.form-section {
    margin-top: 2rem;
    text-align: center;
    padding: 1.5rem;
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-section h3 {
    font-size: 1.8rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1rem;
}

.form-section p {
    font-size: 1rem;
    color: #666;
    margin-bottom: 1.5rem;
}

.styled-form {
    max-width: 400px;
    margin: 0 auto;
    text-align: left;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
}

.form-input {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: border-color 0.3s ease;
}

.form-input:focus {
    border-color: #ea5610;
    outline: none;
}

.submit-button {
    background-color: #ea5610;
    color: white;
    border: none;
    padding: 0.8rem 2rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.2s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.submit-button:hover {
    background-color: #d14e0e;
    transform: translateY(-2px);
}

.submit-button:active {
    background-color: #b9440c;
    transform: translateY(0);
}

.thank-you-section {
    margin-top: 2rem;
    text-align: center;
}

.thank-you-section h3 {
    font-size: 2rem;
    font-weight: 600;
    color: #4caf50;
    margin-bottom: 1rem;
}

.thank-you-section p {
    font-size: 1.2rem;
    color: #333;
    margin-bottom: 1.5rem;
}

.thank-you-button {
    background-color: #ea5610;
    color: white;
    border: none;
    padding: 0.8rem 2rem;
    font-size: 1.2rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.2s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.thank-you-button:hover {
    background-color: #d14e0e;
    transform: translateY(-2px);
}

.thank-you-button:active {
    background-color: #b9440c;
    transform: translateY(0);
}

.loader {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #ea5610;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    animation: spin 1s linear infinite;
    display: inline-block;
    vertical-align: middle;
}

.upload-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-content h4 {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    color: #333;
}

.modal-content button {
    display: block;
    width: 100%;
    margin: 0.5rem 0;
    padding: 0.8rem;
    font-size: 1rem;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    color: white;
    background-color: #ea5610;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.modal-content button:hover {
    background-color: #d14e0e;
}

.modal-content .close-modal {
    background-color: #ccc;
    color: #333;
}

.modal-content .close-modal:hover {
    background-color: #aaa;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
